import React, { useEffect, useRef } from 'react'
import {
  CONDUCTOR_ACCOUNT_PAGE_SIZE,
  conductorAccountChipLabel,
  conductorTabBadge,
} from '../../../conductor/conductorAccountSignal'
import { useGetConductorInstancesQuery, useGetConductorOutboxQuery } from '../../../conductor/conductorApi'

export interface ConductorAccountChipProps {
  subjectCuid: string
  onSelectTab: () => void
  onTabBadge?: (badge: number | string | undefined) => void
}

export const ConductorAccountChip: React.FC<ConductorAccountChipProps> = ({
  subjectCuid,
  onSelectTab,
  onTabBadge,
}) => {
  const query = useGetConductorInstancesQuery({
    subjectCuid,
    page: 1,
    resultsPerPage: CONDUCTOR_ACCOUNT_PAGE_SIZE,
  })
  const instances = query.data?.results ?? []
  const blocked = instances.some((instance) => instance.status === 'BLOCKED')
  const failedOutboxQuery = useGetConductorOutboxQuery(
    {
      subjectCuid,
      status: 'FAILED',
      page: 1,
      resultsPerPage: 1,
    },
    { skip: blocked }
  )
  const instancesReady = query.data != null
  const outboxSettled = blocked || failedOutboxQuery.isSuccess || failedOutboxQuery.isError
  const failedOutbox = failedOutboxQuery.isSuccess && (failedOutboxQuery.data?.total ?? 0) > 0
  const label = query.isError
    ? null
    : !instancesReady
      ? null
      : blocked
        ? 'Blocked'
        : !outboxSettled
          ? null
          : failedOutboxQuery.isError && instances.length === 0
            ? null
            : conductorAccountChipLabel(instances, { failedOutbox })
  const onTabBadgeRef = useRef(onTabBadge)
  onTabBadgeRef.current = onTabBadge

  useEffect(() => {
    if (query.isError) {
      onTabBadgeRef.current?.(undefined)
      return
    }
    if (query.data == null) return
    onTabBadgeRef.current?.(conductorTabBadge(query.data.results))
  }, [query.data, query.isError])

  useEffect(() => {
    return () => {
      onTabBadgeRef.current?.(undefined)
    }
  }, [])

  if (label == null) {
    return null
  }

  return (
    <button
      type="button"
      aria-label="Open Conductor tab"
      onClick={onSelectTab}
      className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-left hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700"
    >
      <span className="text-[10px] font-normal leading-tight text-king-blue dark:text-slate-200 sm:text-xs">
        Conductor
      </span>
      <span className="text-xs font-medium text-slate-800 dark:text-slate-100">{label}</span>
    </button>
  )
}

export default ConductorAccountChip
